import { useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import {
  Link,
  LinkProps,
  NavLink,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Role, useAuthContext } from "@/components/AuthorizationProvider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { useLogin, useLogout, usePrivy } from "@privy-io/react-auth";
import { Button } from "./ui/button";
import { cn } from "@/lib/utils";
import { usePersonasQuery } from "@/api/personas";
import { PersonaImageAvatar } from "@/components/PersonaImageAvatar.tsx";
import { apiUrl, featureQueries } from "@/api/api";
import { clsx } from "clsx";
import { Separator } from "./ui/separator";
import { useAuth } from "@/hooks/use-auth.tsx";
import { useQuery } from "@tanstack/react-query";

const docsUrl = "https://docs.memetica.ai";

function NavbarLink({ className, ...props }: LinkProps) {
  return (
    <NavLink
      className={({ isActive }) =>
        clsx(className, isActive ? "text-accent" : "")
      }
      {...props}
    />
  );
}

export function Navbar() {
  return (
    <>
      <MobileNavbar className="lg:hidden" />
      <DesktopNavbar className="hidden lg:block" />
    </>
  );
}

interface NavbarProps {
  className: string;
}

function MobileNavbar({ className }: NavbarProps) {
  const { authenticated } = usePrivy();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { login } = useLogin({
    onComplete: async ({ wasAlreadyAuthenticated }) => {
      if (!wasAlreadyAuthenticated) {
        navigate("/agents");
      }
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return (
    <nav
      className={clsx("bg-black sticky top-0 left-0 right-0 z-20", className)}
      style={{ overflow: "unset !important" }}
    >
      <div className="px-4">
        <div className="flex justify-between items-center h-12 gap-2">
          <MemeticaLogo className="ml-2" />

          <div className={authenticated ? "" : "hidden"}>
            <Button
              className="hover:bg-primary/25 hover:text-primary"
              variant="ghost"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6 stroke-accent" />
              ) : (
                <Menu className="h-6 w-6 stroke-accent" />
              )}
            </Button>
          </div>
          <div className={authenticated ? "hidden" : ""}>
            <Button
              className="rounded-2xl"
              data-testid="launch-app-button"
              onClick={login}
            >
              Launch
            </Button>
          </div>
        </div>
      </div>

      <div className="relative">
        <div
          className="bg-black left-0 right-0 transition-all duration-300 overflow-hidden lg:hidden flex flex-col items-center gap-1 px-4 shadow-lg z-20"
          style={{ maxHeight: isMenuOpen ? "100vh" : "0" }}
          onClick={() => setIsMenuOpen(false)}
        >
          <NavbarLinks />
        </div>
      </div>
    </nav>
  );
}

function DesktopNavbar({ className }: NavbarProps) {
  const { authenticated } = usePrivy();

  return (
    <nav className={clsx("bg-black w-full relative", className)}>
      <div className="px-4 fixed top-0">
        <div className="flex flex-col items-start justify-between h-16 gap-y-4 mt-3">
          <MemeticaLogo className="ml-2" />

          <div className="w-full flex flex-col justify-end">
            <div
              className={`${
                authenticated ? "-ml-2 hidden lg:flex" : "flex"
              } flex-col gap-1`}
            >
              <NavbarLinks />
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}

function NavbarLinks() {
  const navigate = useNavigate();
  const { userAuthorization } = useAuthContext();
  const { hasAtLeastRole, authenticated, username } = useAuth();
  const { ready } = usePrivy();
  const { data: availablePersonas = [] } = usePersonasQuery();
  const { login } = useLogin({
    onComplete: async ({ wasAlreadyAuthenticated }) => {
      if (!wasAlreadyAuthenticated) {
        navigate("/agents");
      }
    },
    onError: (error) => {
      console.error(error);
    },
  });
  const isAdmin = hasAtLeastRole(Role.TenantAdmin);

  const { data: replyMentionsUsers = [] } = useQuery(
    featureQueries.permissions("reply-mentions"),
  );

  const defaultPersona =
    availablePersonas?.find(
      (p) => p?.persona_id === userAuthorization?.default_persona,
    ) ?? null;

  const isCurrentPath = (path: string) => {
    const currentPath = location.pathname;
    return currentPath.startsWith(`${path}`);
  };

  if (!ready) return null;

  if (authenticated) {
    return (
      <>
        <NavbarLink to="/agents">
          <Button variant="ghost">Agent Management</Button>
        </NavbarLink>
        {(isAdmin || replyMentionsUsers.includes(username)) && (
          <NavbarLink to="/content-management">
            <Button variant="ghost">Content Management</Button>
          </NavbarLink>
        )}
        <NavbarLink to="/create">
          <Button variant="ghost">Content Creation</Button>
        </NavbarLink>
        <NavbarLink to="/autonomous">
          <Button variant="ghost">Agent Automation</Button>
        </NavbarLink>
        <NavbarLink to="/marketplace">
          <Button variant="ghost">Token Marketplace</Button>
        </NavbarLink>

        <NavbarLink to="/content-reviewer">
          <Button variant="ghost">Visuals</Button>
        </NavbarLink>

        <Separator orientation="horizontal" className="md:my-2 -ml-2 " />
        <a href={`${docsUrl}`} target="_blank" rel="noreferrer noopener">
          <Button variant="ghost">Documentation</Button>
        </a>

        <AccountDropdown />
        {/* Default Persona Avatar */}
        {defaultPersona && (
          <NavbarLink to={`/agents/${defaultPersona.persona_id}`}>
            <div
              className={cn(
                "rounded-full p-0.5 transition-all mx-1",
                isCurrentPath(`/agents/${defaultPersona.persona_id}`) &&
                  "border-opacity-100",
              )}
            >
              <PersonaImageAvatar
                src={
                  defaultPersona.avatar_id !== "" &&
                  defaultPersona.avatar_id !== undefined
                    ? `${apiUrl}/get-file?url=persona-knowledge-base/${defaultPersona.persona_id}/${defaultPersona.avatar_id}`
                    : undefined
                }
                altText={defaultPersona.name || "Avatar"}
                className="w-9 h-9 rounded-full"
                gender={defaultPersona.gender}
              />
            </div>
          </NavbarLink>
        )}
      </>
    );
  }

  return (
    <>
      <NavbarLink to="/marketplace">
        <Button variant="ghost">Marketplace</Button>
      </NavbarLink>
      <Separator orientation="horizontal" className="md:my-2" />
      <Button
        className="w-full rounded-2xl"
        data-testid="launch-app-button"
        onClick={login}
      >
        Launch
      </Button>
    </>
  );
}

function AccountDropdown() {
  const { hasAtLeastRole } = useAuth();
  const { logout } = usePrivy();
  const location = useLocation();

  const isCurrentTab = (tab: string) => {
    if (!location.pathname.startsWith("/account")) return false;
    return location.hash === `#${tab}`;
  };

  const isAdmin = hasAtLeastRole(Role.TenantAdmin);

  return (
    <>
      {/* Desktop Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="group !hidden lg:!inline-flex select-none justify-start"
            variant="ghost"
          >
            Account
            <ChevronDown className="ml-2 h-4 w-4 group-data-[state=open]:rotate-180 transition-all" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            className={cn(
              "cursor-pointer hover:bg-accent hover:text-background",
              isCurrentTab("profile") && "text-accent",
            )}
            asChild
            data-testid="profile-dropdown-link"
          >
            <Link to="/account#profile">Profile</Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            className={cn(
              "cursor-pointer hover:bg-accent hover:text-background",
              isCurrentTab("credits") && "text-accent",
            )}
            asChild
          >
            <Link to="/account#credits">Get Credits</Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            className={cn(
              "cursor-pointer hover:bg-accent hover:text-background",
              isCurrentTab("analytics") && "text-accent",
            )}
            asChild
          >
            <Link to="/account#analytics">Analytics</Link>
          </DropdownMenuItem>

          {isAdmin && (
            <DropdownMenuItem
              className={cn(
                "cursor-pointer hover:bg-accent hover:text-background",
                location.pathname.startsWith("/admin") && "text-accent",
              )}
              asChild
            >
              <Link to="/admin">Admin</Link>
            </DropdownMenuItem>
          )}

          <DropdownMenuItem
            className="cursor-pointer hover:bg-accent hover:text-background"
            onClick={logout}
          >
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Mobile Accordion */}
      <Accordion type="single" collapsible>
        <AccordionItem className="border-0" value="settings">
          <AccordionTrigger
            className={cn(
              "flex justify-center lg:hidden no-underline hover:no-underline py-2.5 gap-1 mb-1",
            )}
            onClick={(e) => e.stopPropagation()}
          >
            Account
          </AccordionTrigger>
          <AccordionContent className="pb-0">
            <div className="flex flex-col gap-1 items-center">
              <Link to="/account#profile">
                <Button
                  className={cn(
                    "hover:bg-primary/25 hover:text-primary w-full",
                    isCurrentTab("profile") && "text-accent",
                  )}
                  variant={isCurrentTab("profile") ? "outline" : "ghost"}
                >
                  Profile
                </Button>
              </Link>
              <Link to="/account#credits">
                <Button
                  className={cn(
                    "hover:bg-primary/25 hover:text-primary w-full",
                    isCurrentTab("credits") && "text-accent",
                  )}
                  variant={isCurrentTab("credits") ? "outline" : "ghost"}
                >
                  Get Credits
                </Button>
              </Link>
              <Link to="/account#analytics">
                <Button
                  className={cn(
                    "hover:bg-primary/25 hover:text-primary w-full",
                    isCurrentTab("analytics") && "text-accent",
                  )}
                  variant={isCurrentTab("analytics") ? "outline" : "ghost"}
                >
                  Analytics
                </Button>
              </Link>

              {isAdmin && (
                <Link to="/admin">
                  <Button
                    className={cn(
                      "hover:bg-primary/25 hover:text-primary w-full",
                      location.pathname.startsWith("/admin") && "text-accent",
                    )}
                    variant={
                      location.pathname.startsWith("/admin")
                        ? "outline"
                        : "ghost"
                    }
                  >
                    Admin
                  </Button>
                </Link>
              )}
              <Button
                className="hover:bg-primary/25 hover:text-primary"
                variant="ghost"
                onClick={logout}
              >
                Logout
              </Button>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  );
}

function MemeticaLogo({ className }: { className?: string }) {
  const navigate = useNavigate();

  useLogout({ onSuccess: () => navigate("/") });

  return (
    <NavbarLink className={className} to="/agents">
      <img
        src="/short-logo-white.svg"
        alt="memetica Logo"
        className="cursor-pointer h-6"
      />
    </NavbarLink>
  );
}
